<template>
  <div>
    <!-- Academy selector button -->
    <v-btn
      v-if="showButton"
      :key="`academy-btn-${showButton}`"
      dark
      large
      elevation="4"
      class="academy-work-btn"
      style="padding: 16px"
      @click="openDialog"
    >
      <v-icon left size="20" style="color: white;">
        {{ icons.mdiSchool }}
      </v-icon>
      <span class="btn-text">
        <div class="main-text">WORK ON BEHALF OF ACADEMY</div>
        <div class="sub-text">Click to select academy</div>
      </span>
    </v-btn>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h5">
          Work on behalf of Academy
        </v-card-title>

        <v-card-text>
          <p class="text-body-2 mb-4">
            <strong>Select an academy to work on behalf of.</strong><br>
            All content you create (exercises, assessments, sessions) will be associated with the selected academy only and will not appear in the global list.
          </p>

          <v-autocomplete
            v-model="selectedAcademyLocal"
            :items="academies"
            :loading="isLoading"
            :search-input.sync="searchInput"
            item-text="name"
            item-value="id"
            return-object
            label="Search and select academy"
            placeholder="Type to search academies..."
            clearable
            dense
            outlined
            @input="onAcademySelect"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.location">
                  {{ item.location }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="!selectedAcademyLocal"
            @click="confirmSelection"
          >
            Start Working on Behalf of Academy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { mdiSchool } from '@mdi/js'
import useAcademySelection from '@/composables/useAcademySelection'
import useAcademies from '@/composables/api/academies'

export default {
  name: 'AcademySelector',

  setup() {
    const dialog = ref(false)
    const selectedAcademyLocal = ref(null)
    const searchInput = ref('')
    const forceRender = ref(0)

    // Local reactive copy of selectedAcademy
    const localSelectedAcademy = ref(null)

    // Simple boolean for button visibility
    const showButton = ref(false)

    const { canSelectAcademy, setSelectedAcademy, selectedAcademy } = useAcademySelection()
    const { academies, isLoading, fetchAcademies } = useAcademies()

    // Update button visibility manually
    const updateButtonVisibility = () => {
      const newVisibility = canSelectAcademy.value && !localSelectedAcademy.value
      showButton.value = newVisibility
    }

    // Sync local copy with global
    watch(selectedAcademy, newVal => {
      localSelectedAcademy.value = newVal
      updateButtonVisibility()
    }, { immediate: true })

    watch(canSelectAcademy, (newVal, oldVal) => {
      updateButtonVisibility()
    }, { immediate: true })

    // Initialize visibility
    updateButtonVisibility()

    // Load academies when dialog opens
    watch(dialog, newVal => {
      if (newVal) {
        fetchAcademies()
      }
    })

    const onAcademySelect = academy => {
      selectedAcademyLocal.value = academy
    }

    const openDialog = () => {
      dialog.value = true
    }

    const confirmSelection = () => {
      if (selectedAcademyLocal.value) {
        setSelectedAcademy(selectedAcademyLocal.value)
        closeDialog()
      }
    }

    const closeDialog = () => {
      dialog.value = false
      selectedAcademyLocal.value = null
      searchInput.value = ''
    }

    const filteredAcademies = computed(() => {
      if (!searchInput.value) return academies.value

      return academies.value.filter(academy => academy.name.toLowerCase().includes(searchInput.value.toLowerCase()))
    })

    return {
      dialog,
      selectedAcademyLocal,
      localSelectedAcademy,
      searchInput,
      academies,
      isLoading,
      canSelectAcademy,
      selectedAcademy,
      showButton,
      openDialog,
      onAcademySelect,
      confirmSelection,
      closeDialog,
      filteredAcademies,
      icons: {
        mdiSchool,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.academy-work-btn {
  background: linear-gradient(98deg, #c48eff, var(--v-primary-base) 94%);
//   border: 3px solid white !important;
  box-shadow: 0 4px 12px rgba(255, 111, 0, 0.4) !important;
  animation: pulse 2s infinite;
  margin-right: 16px;

  .btn-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
    color: white;

    .main-text {
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
    }

    .sub-text {
      font-size: 11px;
      opacity: 0.9;
      font-weight: 400;
    }
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(255, 111, 0, 0.6) !important;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 12px rgba(255, 111, 0, 0.4);
  }
  50% {
    box-shadow: 0 4px 16px rgba(255, 111, 0, 0.7);
  }
  100% {
    box-shadow: 0 4px 12px rgba(255, 111, 0, 0.4);
  }
}
</style>
